import { SelectionModel } from '@angular/cdk/collections';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
  inject,
} from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import {
  AbstractDialogResultData,
  AbstractFormDialog,
  DialogService,
  DIALOG_RETURN_TYPES,
} from '@next/core-lib/dialog';
import { AgrDateValidators } from '@next/core-lib/form';
import { LocaleService } from '@next/core-lib/i18n';
import { SiteState } from '@next/core-lib/site';
import { Store, select } from '@ngrx/store';
import { forkJoin, Subscription, merge, of, interval as observableInterval } from 'rxjs';
import { catchError, map, scan, startWith, switchMap, take, takeWhile, tap } from 'rxjs/operators';
import { LoadAvailableTgaCrops } from 'src/app/store/actions';
import { getCropFieldType } from 'src/app/store/selectors';

import { QuickEditDialogModel } from './quick-edit-dialog.model';
import { CropField, CropFieldType, CropVariety, DetailedCropField } from '../../../shared/models';
import { CropVarietyService } from '../../../shared/services/crop-variety.service';
import { LoadCropVarietiesByCropIdsRequest } from '../../store/actions/crop-variety.actions';
import { EditCropField } from '../cropfield-dialog/cropfield-dialog.model';

@Component({
  selector: 'app-quick-edit-dialog',
  templateUrl: './quick-edit-dialog.component.html',
  styleUrls: ['./quick-edit-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class QuickEditDialogComponent
  extends AbstractFormDialog<{ editedCropFields: CropField[]; deletedCropFields: CropField[] }>
  implements OnInit, OnChanges, OnDestroy
{
  data = inject<QuickEditDialogModel>(MAT_DIALOG_DATA);
  protected mdDialogRef = inject<
    MatDialogRef<
      QuickEditDialogComponent,
      AbstractDialogResultData<{
        editedCropFields: CropField[];
        deletedCropFields: CropField[];
      }>
    >
  >(MatDialogRef);
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly fb = inject(FormBuilder);
  private readonly dialog = inject(DialogService);
  private readonly locale = inject(LocaleService);
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly cropVarietyService = inject(CropVarietyService);

  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;

  selection = new SelectionModel<number>(true);
  searchControl = new FormControl();
  isSearching = false;
  subscription = new Subscription();
  query: string;
  cropFields: DetailedCropField[] = [...this.data.cropFields];
  relevantCropFields: DetailedCropField[] = this.cropFields;
  dataSource: DetailedCropField[];
  deletedCropFieldIds: number[] = [];
  soilAnalysesFormArrayByCropFieldId: { [cropFieldId: number]: FormArray } = this.cropFields.reduce(
    (a, f) => ({ ...a, [f.id]: this.fb.array(f.soilAnalysisIds) }),
    {},
  );
  formGroupByCropFieldId: { [cropFieldId: number]: FormGroup } = this.cropFields.reduce(
    (a, f) => ({
      ...a,
      [f.id]: this.createCropFieldFormGroup(f),
    }),
    {},
  );
  renderQuickEditFormComponents = false;
  bemCropVarietiesByCropId: Record<number, CropVariety[] | undefined> = {};
  pageSize = 10;

  private readonly cropFieldType$ = this.store.pipe(select(getCropFieldType));
  modeBadge$ = this.cropFieldType$.pipe(
    map((cropFieldType) => {
      const modeLabel =
        cropFieldType === CropFieldType.ManureSpace
          ? this.locale.translate('manureSpace')
          : this.locale.translate('cultivationPlan');
      return `${modeLabel} | ${this.data.cropYear}`;
    }),
  );
  modeBadgeBgColor$ = this.cropFieldType$.pipe(
    map((cropFieldType) => (cropFieldType === CropFieldType.ManureSpace ? '#fff9c4' : '#c8e6c9')),
  );

  private formArray = this.fb.array(Object.values(this.formGroupByCropFieldId));
  formGroup = this.fb.group({
    cropFields: this.formArray,
  });

  ngOnInit(): void {
    // Loads all cropvarieties in bulk for all existing crops on cropfields, so that
    // the app-cropfield-detail components don't have to request those one-by-one.
    setTimeout(() => this.loadCropVarieties(this.cropFields.map((f) => f.cropId)), 0);
    this.loadAvailableBemCropVarieties();

    // UGLY: This is not the nicest solution, but very effective. For customers with a lot
    // of cropfields, the quick edit dialog may be VERY slow to render that many form components.
    // For this purpose, we delay the rendering by 50ms and show a loading spinner, so the
    // quick edit dialog can instantly open && the user sees a CSS-animated loading spinner
    // while the UI is frozen due to the large amount of Angular render operations.
    setTimeout(() => {
      this.renderQuickEditFormComponents = true;
      this.cdRef.detectChanges();
    }, 50);

    this.subscription.add(
      this.searchControl.valueChanges.subscribe((query: string) => {
        if (!!query !== this.isSearching) {
          this.isSearching = !!query;
        }
        this.isSearching = !!query;
        this.query = query;
        this.updateRelevantCropFields(query);
      }),
    );

    setTimeout(() => {
      this.store.dispatch(new LoadAvailableTgaCrops());
    }, 0);
    this.linkListToPaginator();
  }

  updateRelevantCropFields(query: string) {
    query = query?.toLowerCase() ?? '';
    const cropFields = this.cropFields.filter((f) => !this.deletedCropFieldIds.includes(f.id));
    this.relevantCropFields = query
      ? cropFields.filter(
          (cropField) =>
            cropField.name.toString().toLowerCase().indexOf(query) !== -1 ||
            cropField.cropName.toString().toLowerCase().indexOf(query) !== -1,
        )
      : cropFields;
    this.linkListToPaginator();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['values']) {
      this.searchControl.enable({ emitEvent: true });
    }
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  trackByCropField(_: number, cropField: CropField) {
    return cropField.id;
  }

  addDaysToDate(date: Date, days: number) {
    date.setDate(date.getDate() + days);
    return date;
  }

  getSelectedCount = (): number => this.selection.selected.length;

  areAllSelected = (): boolean => this.getSelectedCount() === this.cropFields.length;

  areAllSelectedFromPage = (): boolean => this.getSelectedCount() === this.dataSource.length;

  areAnySelected = (): boolean => this.getSelectedCount() > 0;

  areSomeSelected = (): boolean => this.areAnySelected() && !this.areAllSelected();

  masterToggle() {
    if (this.areAllSelectedFromPage()) {
      this.selection.clear();
    } else {
      this.selectAllFromPage();
    }
  }

  selectAllFromPage(): void {
    this.dataSource.forEach((cropField) => this.selection.select(cropField.id));
  }

  selectAll(): void {
    this.cropFields.forEach((cropField) => this.selection.select(cropField.id));
  }

  clearAll(): void {
    this.selection.clear();
  }

  closeSearch() {
    this.isSearching = false;
    this.searchControl.setValue('');
  }

  confirmEditCropFields(data: { cropFields: EditCropField[] }) {
    const editedCropFields: CropField[] = [];
    const deletedCropFields: CropField[] = [];

    data.cropFields.forEach((editCropField) => {
      const cropField = this.cropFields.find((f) => f.id === editCropField.id);
      if (this.deletedCropFieldIds.includes(cropField.id)) {
        deletedCropFields.push(cropField);
      } else {
        const formGroup = this.formGroupByCropFieldId[editCropField.id];
        if (formGroup?.dirty) {
          const resultCropField = this.getResultCropField(editCropField, cropField);
          editedCropFields.push(resultCropField);
        }
      }
    });

    this.confirm({ editedCropFields, deletedCropFields });
  }

  getResultCropField(editCropField: EditCropField, cropField: CropField): CropField {
    const resultCropField = {
      ...cropField,
      ...editCropField,
      startDate: new Date(
        Date.UTC(
          editCropField.startDate.getFullYear(),
          editCropField.startDate.getMonth(),
          editCropField.startDate.getDate(),
        ),
      ).toISOString(),
      endDate: new Date(
        Date.UTC(
          editCropField.endDate.getFullYear(),
          editCropField.endDate.getMonth(),
          editCropField.endDate.getDate(),
        ),
      ).toISOString(),
    };

    if (editCropField.outsideDutchBorders) {
      resultCropField.province = null;
    }

    return resultCropField;
  }

  hasSelectedTgaParticipatingCropFields(): boolean {
    return this.cropFields
      .filter(({ id }) => this.selection.selected.indexOf(id) !== -1)
      .some((cropField) => cropField.isParticipatingToTga === true);
  }

  deleteSelectedCropFields() {
    const hasSelectedTgaParticipatingCropFields = this.hasSelectedTgaParticipatingCropFields();

    this.cropFields
      .filter(({ id }) => id < 1)
      .forEach((field) => {
        if (this.selection.selected.indexOf(field.id) !== -1) {
          delete this.soilAnalysesFormArrayByCropFieldId[field.id];
          delete this.formGroupByCropFieldId[field.id];
          const index = this.cropFields.indexOf(field);
          this.cropFields.splice(index, 1);
          this.formArray.removeAt(index);
          this.selection.toggle(field.id);
        }
      });

    if (this.selection.selected.length) {
      let dialogText = this.locale.translate(
        this.selection.selected.length > 1 ? 'removeCropFields' : 'removeCropField',
      );
      if (hasSelectedTgaParticipatingCropFields) {
        dialogText = this.locale.translate(
          this.selection.selected.length > 1
            ? 'cropFieldsWithTgaAdviceDeleteWarning'
            : 'cropFieldWithTgaAdviceDeleteWarning',
        );
      }

      this.dialog
        .confirm({
          title: this.locale.translate('remove'),
          text: dialogText,
          pushHistoryState: false,
        })
        .pipe(take(1))
        .subscribe((x) => {
          if (x.status === DIALOG_RETURN_TYPES.confirm) {
            this.selection.selected.forEach((id) => this.deletedCropFieldIds.push(id));
            this.selection.clear();
            this.updateRelevantCropFields(this.query);
          }
        });
    }
  }

  linkListToPaginator(): void {
    if (this.paginator) {
      merge(this.paginator.page)
        .pipe(
          startWith({}),
          switchMap(() => of(this.relevantCropFields)),
        )
        .subscribe((res) => {
          const from = this.paginator.pageIndex * this.pageSize;
          const to = from + this.pageSize;
          this.dataSource = res.slice(from, to);
        });
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  handlePageEvent(event: PageEvent, element: any) {
    this.pageSize = event.pageSize;
    const duration = 600;
    const interval = 5;
    const move = (element.scrollTop * interval) / duration;
    observableInterval(interval)
      .pipe(
        scan((acc) => acc - move, element.scrollTop),
        tap((position) => (element.scrollTop = position)),
        takeWhile((val) => val > 0),
      )
      .subscribe();
  }

  private createCropFieldFormGroup(f: DetailedCropField) {
    return this.fb.group({
      id: f.id,
      name: [f.name, [Validators.required, Validators.minLength(1), Validators.maxLength(100)]],
      areaInHectares: [
        f.areaInHectares,
        [Validators.required, Validators.min(0.001), Validators.max(100000)],
      ],
      isAreaCalculated: [f.isAreaCalculated],
      cropId: [f.cropId, [Validators.required]],
      cropVarietyId: [f.cropVarietyId],
      conceptId: [f.conceptId ?? null],
      chainPartnerId: [f.chainPartnerId ?? null],
      soilTypeId: [f.soilTypeId, [Validators.required]],
      province: [f.province, f.outsideDutchBorders ? [] : [Validators.required]],
      machiningDepthInCentimeters: [
        f.machiningDepthInCentimeters,
        [Validators.required, Validators.min(0), Validators.max(30)],
      ],
      startDate: [
        f.startDate ? new Date(f.startDate) : null,
        this.getStartDateValidators(f.endDate ? new Date(f.endDate) : null),
      ],
      endDate: [
        f.endDate ? new Date(f.endDate) : null,
        this.getEndDateValidators(f.startDate ? new Date(f.startDate) : null),
      ],
      isSkal: f.isSkal,
      isGroundWaterPreservation: f.isGroundWaterPreservation,
      isPlanetProof: f.isPlanetProof,
      isSandySoil: f.isSandySoil,
      isRentedField: f.isRentedField,
      outsideDutchBorders: f.outsideDutchBorders,
      feature: f.feature,
      soilAnalysisIds: this.soilAnalysesFormArrayByCropFieldId[f.id],
      isNutrientsPollutedArea: f.isNutrientsPollutedArea,
      isGroundWaterPreservationWithNitrogenCuts: f.isGroundWaterPreservationWithNitrogenCuts,
    });
  }

  private getStartDateValidators(endDate: Date) {
    const validators = [Validators.required];
    if (endDate) {
      validators.push(AgrDateValidators.maxDate(endDate));
    }
    return validators;
  }

  private getEndDateValidators(startDate: Date) {
    const validators = [Validators.required];
    if (startDate) {
      validators.push(AgrDateValidators.minDate(startDate));
    }
    validators.push(AgrDateValidators.maxDate(new Date(9999, 12, 31)));
    return validators;
  }

  private loadCropVarieties(cropIds: number[]) {
    if (cropIds.length > 0) {
      this.store.dispatch(new LoadCropVarietiesByCropIdsRequest(cropIds));
    }
  }

  private loadAvailableBemCropVarieties(): void {
    const cropFieldsByCropId = this.cropFields
      .filter((cropField) => this.canChangeBemCropVariety(cropField))
      .filter(
        (cropField, index, cropFields) =>
          cropFields.findIndex(({ cropId }) => cropId === cropField.cropId) === index,
      );

    if (cropFieldsByCropId.length === 0) {
      return;
    }

    this.subscription.add(
      forkJoin(
        cropFieldsByCropId.map((cropField) =>
          this.cropVarietyService.getAvailableBemCropVarieties(cropField.id).pipe(
            map((cropVarieties) => ({ cropId: cropField.cropId, cropVarieties })),
            catchError(() => of({ cropId: cropField.cropId, cropVarieties: [] as CropVariety[] })),
          ),
        ),
      ).subscribe((results) => {
        this.bemCropVarietiesByCropId = results.reduce(
          (cropVarietiesByCropId, { cropId, cropVarieties }) => ({
            ...cropVarietiesByCropId,
            [cropId]: cropVarieties,
          }),
          {} as Record<number, CropVariety[] | undefined>,
        );
        this.cdRef.markForCheck();
      }),
    );
  }

  private canChangeBemCropVariety(cropField: CropField): boolean {
    return (
      cropField.isParticipatingToBem === true &&
      cropField.isCancellingBem !== true &&
      cropField.isPlantDateConfirmed !== true
    );
  }
}
